import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Mail, MessageSquare, MessageCircle, Search, Copy } from "lucide-react";
import { staffApi } from "@/lib/staffApi";
import { Skeleton, ErrorState } from "@/components/BookingShell";
import { Pill, CHANNEL_LABEL, CATEGORY_LABEL } from "./shared";

const CHANNEL_ICON = { email: Mail, sms: MessageSquare, whatsapp: MessageCircle };

export function CommsTemplates() {
    const nav = useNavigate();
    const [templates, setTemplates] = useState(null);
    const [meta, setMeta] = useState(null);
    const [error, setError] = useState(null);
    const [q, setQ] = useState("");
    const [channelFilter, setChannelFilter] = useState("all");
    const [statusFilter, setStatusFilter] = useState("active"); // Sprint 30: default hides archived
    const [showNew, setShowNew] = useState(false);
    const [busy, setBusy] = useState(false);
    const [form, setForm] = useState({ name: "", channel: "email", category: "transactional" });

    const load = () => {
        setError(null);
        Promise.all([staffApi.commsListTemplates(), staffApi.commsMeta()])
            .then(([tr, mr]) => { setTemplates(tr.templates || []); setMeta(mr); })
            .catch(setError);
    };
    useEffect(load, []);

    const filtered = useMemo(() => {
        let list = templates || [];
        if (channelFilter !== "all") list = list.filter((t) => t.channel === channelFilter);
        // Sprint 30: "active" is a convenience meta-filter for "not archived".
        if (statusFilter === "active") {
            list = list.filter((t) => t.status !== "archived");
        } else if (statusFilter !== "all") {
            list = list.filter((t) => t.status === statusFilter);
        }
        if (q) list = list.filter((t) => (t.name || "").toLowerCase().includes(q.toLowerCase()));
        return list;
    }, [templates, channelFilter, statusFilter, q]);

    const create = async () => {
        if (!form.name.trim() || busy) return;
        setBusy(true);
        try {
            const r = await staffApi.commsCreateTemplate(form);
            toast.success("Template created");
            setShowNew(false);
            setForm({ name: "", channel: "email", category: "transactional" });
            nav(`/staff/communications/templates/${r.template.id}`);
        } catch (e) { toast.error(e.message || "Create failed"); }
        finally { setBusy(false); }
    };

    const duplicate = async (id) => {
        setBusy(true);
        try { await staffApi.commsDuplicateTemplate(id); toast.success("Duplicated"); load(); }
        catch (e) { toast.error(e.message || "Duplicate failed"); }
        finally { setBusy(false); }
    };

    if (error) return <ErrorState error={error} onRetry={load} />;

    return (
        <div className="space-y-4" data-testid="comms-templates-page">
            <div className="flex flex-wrap items-center gap-2 justify-between">
                <div className="flex flex-wrap gap-2">
                    <div className="relative">
                        <Search className="h-3.5 w-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
                        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search templates" data-testid="template-search"
                               className="pl-9 pr-3 h-9 rounded-full bg-transparent border hairline text-sm text-white placeholder-white/40 focus:border-gold outline-none" />
                    </div>
                    <select value={channelFilter} onChange={(e) => setChannelFilter(e.target.value)} data-testid="filter-channel"
                            className="h-9 rounded-full bg-transparent border hairline text-xs text-white/80 px-3">
                        <option value="all">All channels</option>
                        {(meta?.channels || []).map((c) => <option key={c} value={c}>{CHANNEL_LABEL[c]}</option>)}
                    </select>
                    <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} data-testid="filter-status"
                            className="h-9 rounded-full bg-transparent border hairline text-xs text-white/80 px-3">
                        <option value="active">Active (not archived)</option>
                        <option value="all">All statuses</option>
                        {(meta?.template_statuses || []).map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                </div>
                <button onClick={() => setShowNew(true)} data-testid="new-template-btn"
                        className="h-9 px-4 rounded-full bg-gold text-navy font-semibold text-xs inline-flex items-center gap-1">
                    <Plus className="h-3.5 w-3.5" /> New template
                </button>
            </div>

            {!templates ? <Skeleton className="h-40" /> : filtered.length === 0 ? (
                <div className="rounded-2xl border hairline p-8 text-center" data-testid="templates-empty">
                    <p className="text-white/60 text-sm">No templates yet. Draft your first one to get started.</p>
                </div>
            ) : (
                <ul className="space-y-2" data-testid="templates-list">
                    {filtered.map((t) => {
                        const Icon = CHANNEL_ICON[t.channel] || Mail;
                        return (
                            <li key={t.id} className="rounded-2xl border hairline p-4 hover:border-gold/50 cursor-pointer transition-all flex items-center gap-3"
                                onClick={() => nav(`/staff/communications/templates/${t.id}`)}
                                data-testid={`template-row-${t.id}`}>
                                <div className="h-10 w-10 rounded-full glass flex items-center justify-center shrink-0">
                                    <Icon className="h-4 w-4 text-gold" strokeWidth={1.5} />
                                </div>
                                <div className="min-w-0 flex-1">
                                    <div className="text-white font-ui text-sm truncate">{t.name}</div>
                                    <div className="text-[10px] uppercase tracking-[0.16em] text-white/45 flex flex-wrap gap-2 mt-0.5">
                                        <span>{CHANNEL_LABEL[t.channel]}</span>
                                        <span>·</span>
                                        <span>{CATEGORY_LABEL[t.category] || t.category}</span>
                                        <span>·</span>
                                        <span>v{t.version}</span>
                                    </div>
                                </div>
                                <Pill value={t.status} />
                                <button onClick={(e) => { e.stopPropagation(); duplicate(t.id); }} disabled={busy}
                                        data-testid={`duplicate-${t.id}`}
                                        className="h-8 px-2 rounded-full border hairline text-[11px] text-white/70 hover:text-gold inline-flex items-center gap-1">
                                    <Copy className="h-3 w-3" /> Duplicate
                                </button>
                            </li>
                        );
                    })}
                </ul>
            )}

            {showNew && (
                <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center px-4" data-testid="new-template-modal">
                    <div className="w-full max-w-md rounded-3xl glass-strong p-6 space-y-3">
                        <h3 className="font-serif text-2xl text-white">New template</h3>
                        <label className="block">
                            <div className="text-[10px] uppercase tracking-[0.24em] text-white/45 mb-1.5">Name</div>
                            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="new-template-name"
                                   placeholder="e.g. 24h Reminder — Email"
                                   className="w-full bg-transparent border-b hairline py-2 text-white text-sm outline-none focus:border-gold" />
                        </label>
                        <div className="grid grid-cols-2 gap-3">
                            <label className="block">
                                <div className="text-[10px] uppercase tracking-[0.24em] text-white/45 mb-1.5">Channel</div>
                                <select value={form.channel} onChange={(e) => setForm({ ...form, channel: e.target.value })} data-testid="new-template-channel"
                                        className="w-full bg-transparent border-b hairline py-2 text-white text-sm outline-none">
                                    {(meta?.channels || ["email","sms","whatsapp"]).map((c) => <option key={c} value={c} className="bg-navy">{CHANNEL_LABEL[c]}</option>)}
                                </select>
                            </label>
                            <label className="block">
                                <div className="text-[10px] uppercase tracking-[0.24em] text-white/45 mb-1.5">Category</div>
                                <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="new-template-category"
                                        className="w-full bg-transparent border-b hairline py-2 text-white text-sm outline-none">
                                    {(meta?.template_categories || Object.keys(CATEGORY_LABEL)).map((c) => (
                                        <option key={c} value={c} className="bg-navy">{CATEGORY_LABEL[c] || c}</option>
                                    ))}
                                </select>
                            </label>
                        </div>
                        <div className="flex gap-2 justify-end pt-3">
                            <button onClick={() => setShowNew(false)} className="h-10 rounded-full border hairline text-white/70 px-4 text-sm">Cancel</button>
                            <button onClick={create} disabled={busy || !form.name.trim()} data-testid="create-template-confirm"
                                    className="h-10 rounded-full bg-gold text-navy px-5 text-sm font-semibold disabled:opacity-40">
                                Create draft
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
